import { t } from "@lingui/macro";
import { Button } from "@reactive-resume/ui";
import { Link, useRouteError } from "react-router";

import { LocaleProvider } from "@/client/providers/locale";

type RouterError = {
  statusText?: string;
  message?: string;
  status: number;
  data: string;
} & Error;

const getErrorTitle = (status: number) => {
  switch (status) {
    case 404: {
      return t`Lost at sea`;
    }
    case 403: {
      return t`Access denied`;
    }
    case 500: {
      return t`Engine trouble`;
    }
    case 401: {
      return t`Sign in required`;
    }
    case 400: {
      return t`Bad request`;
    }
    default: {
      return t`Something went wrong`;
    }
  }
};

const getErrorMessage = (status: number) => {
  switch (status) {
    case 404: {
      return t`The page you're looking for has drifted off the map. Let's get you back to shore.`;
    }
    case 403: {
      return t`You don't have permission to access this page.`;
    }
    case 500: {
      return t`Our engines are sputtering. We're working on it.`;
    }
    case 401: {
      return t`You need to sign in to reach this page.`;
    }
    case 400: {
      return t`That request didn't quite make sense.`;
    }
    default: {
      return t`An unexpected error occurred.`;
    }
  }
};

export const ErrorPage = () => {
  const error = useRouteError() as RouterError;
  const statusCode = error.status;

  return (
    <LocaleProvider>
      <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-b from-sky-50 via-sky-100 to-blue-200 p-4 dark:from-slate-900 dark:via-slate-950 dark:to-blue-950">
        {/* Animated wave layers */}
        <svg
          className="pointer-events-none absolute bottom-0 left-0 w-[200%] animate-[wave_18s_linear_infinite] opacity-40"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,60 C240,100 480,20 720,60 C960,100 1200,20 1440,60 L1440,120 L0,120 Z"
            fill="hsl(210 90% 60%)"
          />
        </svg>
        <svg
          className="pointer-events-none absolute bottom-0 left-0 w-[200%] animate-[wave_12s_linear_infinite_reverse] opacity-60"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,80 C320,40 640,120 960,80 C1280,40 1440,80 1440,80 L1440,120 L0,120 Z"
            fill="hsl(210 80% 50%)"
          />
        </svg>

        {/* Floating bottle / boat */}
        <div className="pointer-events-none absolute bottom-24 left-1/2 -translate-x-1/2 animate-[bob_4s_ease-in-out_infinite]">
          <svg width="80" height="80" viewBox="0 0 80 80" aria-hidden="true">
            <g transform="rotate(-8 40 40)">
              {/* Bottle body */}
              <rect
                x="28"
                y="26"
                width="24"
                height="36"
                rx="4"
                fill="hsl(45 90% 70%)"
                stroke="hsl(30 60% 30%)"
                strokeWidth="1.5"
              />
              {/* Bottle neck */}
              <rect
                x="34"
                y="14"
                width="12"
                height="14"
                rx="2"
                fill="hsl(45 90% 70%)"
                stroke="hsl(30 60% 30%)"
                strokeWidth="1.5"
              />
              {/* Cork */}
              <rect x="33" y="10" width="14" height="6" rx="1" fill="hsl(25 50% 35%)" />
              {/* Note inside */}
              <rect x="32" y="34" width="16" height="20" fill="hsl(40 30% 95%)" />
              <line x1="35" y1="40" x2="45" y2="40" stroke="hsl(220 40% 40%)" strokeWidth="0.8" />
              <line x1="35" y1="44" x2="45" y2="44" stroke="hsl(220 40% 40%)" strokeWidth="0.8" />
              <line x1="35" y1="48" x2="42" y2="48" stroke="hsl(220 40% 40%)" strokeWidth="0.8" />
            </g>
          </svg>
        </div>

        {/* Sun/cloud */}
        <div className="pointer-events-none absolute right-12 top-12 animate-[drift_20s_ease-in-out_infinite] opacity-70">
          <svg width="60" height="40" viewBox="0 0 60 40" aria-hidden="true">
            <ellipse cx="20" cy="22" rx="14" ry="8" fill="white" />
            <ellipse cx="34" cy="18" rx="18" ry="10" fill="white" />
            <ellipse cx="46" cy="24" rx="12" ry="7" fill="white" />
          </svg>
        </div>

        {/* Content card */}
        <div className="relative z-10 w-full max-w-md space-y-6 rounded-2xl border border-border/40 bg-background/80 p-8 text-center shadow-xl backdrop-blur-md">
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">{t`Error ${statusCode}`}</p>
            <h1 className="font-serif text-3xl italic text-foreground">
              {getErrorTitle(statusCode)}
            </h1>
            {error.statusText && (
              <p className="text-xs font-medium text-muted-foreground">{error.statusText}</p>
            )}
          </div>

          <p className="text-sm text-muted-foreground">
            {error.data || error.message || getErrorMessage(statusCode)}
          </p>

          <div className="flex justify-center gap-2">
            <Button asChild>
              <Link to="/">{t`Back to shore`}</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              onClick={() => {
                window.history.back();
              }}
            >
              <button type="button">{t`Go back`}</button>
            </Button>
          </div>
        </div>

        {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
        <style>{`
          @keyframes wave {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @keyframes bob {
            0%, 100% { transform: translate(-50%, 0) rotate(-3deg); }
            50% { transform: translate(-50%, -10px) rotate(3deg); }
          }
          @keyframes drift {
            0%, 100% { transform: translateX(0); }
            50% { transform: translateX(-30px); }
          }
        `}</style>
      </main>
    </LocaleProvider>
  );
};
